// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Login gateway  (route /welcome — mobile number → SMS code → guest option)
// Firebase Phone Authentication: Google sends the SMS one-time code and checks it, so the
// browser talks to Firebase Auth directly (no ZenFresh API call is involved in login).
// Bot protection is Firebase's own invisible reCAPTCHA, created per send below.
// Test numbers (fixed codes) are configured in the Firebase console, not in this repo.
// ─────────────────────────────────────────────────────────────────────────────
import type { ConfirmationResult, RecaptchaVerifier } from "firebase/auth";
import { getFirebaseAuth } from "./firebaseAuth";

let verifier: RecaptchaVerifier | null = null;
let container: HTMLElement | null = null;

/** Tear down the reCAPTCHA widget; a used verifier must not be reused for the next send. */
function clearVerifier() {
  try {
    verifier?.clear();
  } catch {
    // Already cleared or never rendered: nothing to do.
  }
  container?.remove();
  verifier = null;
  container = null;
}

/**
 * Asks Firebase to SMS a 6-digit code to +91<nationalNumber>. Resolves with the pending
 * confirmation to pass to `confirmCode`; rejects with a FirebaseError (see errorMessage()).
 */
export async function sendSmsCode(nationalNumber: string): Promise<ConfirmationResult> {
  const [auth, { RecaptchaVerifier: Verifier, signInWithPhoneNumber }] = await Promise.all([
    getFirebaseAuth(),
    import("firebase/auth"),
  ]);
  clearVerifier();
  container = document.createElement("div");
  document.body.appendChild(container);
  verifier = new Verifier(auth, container, { size: "invisible" });
  try {
    return await signInWithPhoneNumber(auth, `+91${nationalNumber}`, verifier);
  } catch (error) {
    clearVerifier();
    throw error;
  }
}

/** Firebase's stable error code (e.g. "auth/invalid-verification-code"), if this is one. */
export function firebaseErrorCode(error: unknown): string | null {
  if (typeof error === "object" && error !== null && "code" in error) {
    const code = (error as { code: unknown }).code;
    if (typeof code === "string" && code.startsWith("auth/")) return code;
  }
  return null;
}
